import { CDN_CONFIG, NETWORK_CONFIG, BROWSER_SUPPORT, ENV_CONFIG } from '../config';

/**
 * Utility functions for media optimization and CDN delivery
 */

export interface ImageOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'auto' | 'avif' | 'webp' | 'jpg' | 'png';
  fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
  blur?: number;
  grayscale?: boolean;
}

export interface VideoOptions {
  quality?: '4k' | '1080p' | '720p' | '480p';
  format?: 'auto' | 'webm' | 'mp4';
  codec?: 'auto' | 'vp9' | 'vp8' | 'av1' | 'hevc' | 'h264';
  startTime?: number;
  duration?: number;
}

/**
 * Generate optimized image URL with CDN transformations
 */
export function getOptimizedImageUrl(
  src: string,
  options: ImageOptions = {}
): string {
  if (!src) return '';

  const {
    width,
    height,
    quality = CDN_CONFIG.imageOptimization.quality,
    format = 'auto',
    fit = 'cover',
    blur,
    grayscale
  } = options;

  // Always use local assets for now since CDN setup is not complete
  const localSrc = src.startsWith('/') ? src : `/${src}`;
  return localSrc;
}

/**
 * Generate responsive image srcset for different screen sizes
 */
export function generateResponsiveSrcSet(
  src: string,
  options: ImageOptions = {}
): string {
  // For now, just return the original source since CDN is not set up
  return getOptimizedImageUrl(src, options);
}

/**
 * Generate video sources for different formats and qualities
 */
export function generateVideoSources(
  src: string,
  options: VideoOptions = {}
): Array<{ src: string; type: string; quality: string }> {
  const sources = [];
  
  // Add local fallback sources
  const baseSrc = src.replace(/\.[^/.]+$/, '');
  
  sources.push({
    src: `${baseSrc}.webm`,
    type: 'video/webm',
    quality: 'standard'
  });

  sources.push({
    src: `${baseSrc}.mp4`,
    type: 'video/mp4',
    quality: 'fallback'
  });

  return sources;
}

/**
 * Check if browser supports a specific media format
 */
export function supportsBrowserFormat(format: keyof typeof BROWSER_SUPPORT): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const element = document.createElement(format === 'webm' || format === 'mp4' ? 'video' : 'canvas');
    
    if (format in BROWSER_SUPPORT) {
      const mimeType = BROWSER_SUPPORT[format];
      
      if ('canPlayType' in element && typeof mimeType === 'string') {
        const support = (element as HTMLVideoElement).canPlayType(mimeType);
        return support === 'probably' || support === 'maybe';
      }

      if ('toDataURL' in element && typeof mimeType === 'string' && format.includes('image/')) {
        try {
          return (element as HTMLCanvasElement).toDataURL(mimeType).indexOf('data:' + mimeType) === 0;
        } catch {
          return false;
        }
      }
    }
  } catch (error) {
    console.warn(`Error checking browser support for ${format}:`, error);
    return false;
  }

  return false;
}

/**
 * Get preferred video qualities based on network conditions
 */
export function getPreferredQualities(): string[] {
  if (typeof window === 'undefined') return ['1080p', '720p'];

  try {
    const connection = (navigator as any).connection;
    
    if (!connection) return ['1080p', '720p'];

    const { effectiveType, saveData } = connection;

    if (saveData || NETWORK_CONFIG.slowConnection.includes(effectiveType)) {
      return ['720p', '480p'];
    }

    if (NETWORK_CONFIG.moderateConnection.includes(effectiveType)) {
      return ['1080p', '720p'];
    }

    // Fast connection
    return ['4k', '1080p', '720p'];
  } catch (error) {
    console.warn('Error getting network conditions:', error);
    return ['1080p', '720p'];
  }
}

/**
 * Get optimal image format based on browser support
 */
export function getOptimalImageFormat(): string {
  if (supportsBrowserFormat('avif')) return 'avif';
  if (supportsBrowserFormat('webp')) return 'webp';
  return 'jpg';
}

/**
 * Preload critical images for better performance
 */
export function preloadCriticalImages(images: string[]): void {
  if (typeof window === 'undefined') return;

  images.forEach(src => {
    try {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src.startsWith('/') ? src : `/${src}`;
      document.head.appendChild(link);
    } catch (error) {
      console.warn(`Failed to preload image ${src}:`, error);
    }
  });
}

/**
 * Lazy load non-critical images
 */
export function setupImageLazyLoading(): void {
  if (typeof window === 'undefined') return;
  
  try {
    // Use native lazy loading if supported
    if ('loading' in HTMLImageElement.prototype) {
      return; // Browser handles it natively
    }

    // Fallback to Intersection Observer
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          }
        });
      }, {
        rootMargin: '100px'
      });

      // Observe all images with data-src
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  } catch (error) {
    console.warn('Failed to setup lazy loading:', error);
  }
}

/**
 * Monitor and log performance metrics
 */
export function monitorMediaPerformance(): void {
  if (!ENV_CONFIG.enablePerformanceMonitoring || typeof window === 'undefined') return;

  try {
    // Monitor image loading performance
    if ('PerformanceObserver' in window) {
      const imageObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach(entry => {
          if ((entry as PerformanceResourceTiming).initiatorType === 'img') {
            console.log(`Image loaded: ${entry.name} - Duration: ${entry.duration.toFixed(2)}ms`);
          }
        });
      });

      imageObserver.observe({ entryTypes: ['resource'] });
    }

    // Monitor video loading performance
    document.addEventListener('loadstart', (event) => {
      if (event.target instanceof HTMLVideoElement) {
        const startTime = performance.now();
        
        event.target.addEventListener('loadeddata', () => {
          const loadTime = performance.now() - startTime;
          console.log(`Video loaded: ${(event.target as HTMLVideoElement).src} - Duration: ${loadTime.toFixed(2)}ms`);
        }, { once: true });
      }
    });
  } catch (error) {
    console.warn('Failed to setup performance monitoring:', error);
  }
}

/**
 * Setup service worker for media caching
 */
export function setupMediaCaching(): void {
  if (!ENV_CONFIG.isProduction || !CDN_CONFIG.performance.enableServiceWorker) return;
  
  try {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('Media caching service worker registered:', registration);
        })
        .catch(error => {
          console.error('Service worker registration failed:', error);
        });
    }
  } catch (error) {
    console.warn('Failed to setup service worker:', error);
  }
}

/**
 * Initialize media optimization utilities
 */
export function initializeMediaOptimization(): void {
  if (typeof window === 'undefined') return;

  try {
    setupImageLazyLoading();
    
    // Only setup monitoring and caching in production
    if (ENV_CONFIG.isProduction) {
      monitorMediaPerformance();
      setupMediaCaching();
    }

    // Preload critical hero images
    const heroImages = [
      '/portfolio/Propertymasters.png',
      '/portfolio/sorento.png',
      '/portfolio/nebenki.de.png',
      '/portfolio/Litholog.png',
      '/portfolio/racketbuild.png'
    ];
    
    preloadCriticalImages(heroImages);
  } catch (error) {
    console.warn('Failed to initialize media optimization:', error);
  }
}
